/*
 * Grade de compra múltipla por variante — Ariel Malhas
 *
 * Estado indexado por ID de variante (nunca pelo índice visual), então trocar
 * a espessura ou reordenar a grade não embaralha as quantidades escolhidas.
 *
 * A adição é feita em UMA requisição em lote e dispara o mesmo evento de
 * carrinho do tema (shopify:cart:lines-update), então popup, drawer, badge e
 * analytics reagem exatamente como em qualquer outro add-to-cart.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

class VariantGrid {
  constructor(root) {
    this.root = root;
    this.cartAddUrl = root.dataset.cartAddUrl || '/cart/add';
    this.cartUrl = root.dataset.cartUrl || '/cart';
    this.buyBtn = root.querySelector('[data-avg-buy]');
    this.buyLabel = root.querySelector('[data-avg-buy-label]');
    this.status = root.querySelector('[data-avg-status]');
    /** @type {Map<string, number>} quantidade por ID de variante */
    this.qty = new Map();
    this.busy = false;
  }

  init() {
    // Delegação: um listener por grade, não um por cartão.
    this.root.addEventListener('change', (e) => {
      const sel = e.target.closest && e.target.closest('[data-avg-qty]');
      if (sel) this.onQtyChange(sel);
    });

    this.root.addEventListener('click', (e) => {
      if (!e.target.closest) return;
      const pill = e.target.closest('[data-primary-pill]');
      if (pill) return this.onPrimary(pill);

      const notify = e.target.closest('[data-avg-notify]');
      if (notify) return this.onNotify(notify);

      const buy = e.target.closest('[data-avg-buy]');
      if (buy) return this.onBuy();
    });

    this.refresh();
  }

  card(el) {
    return el.closest('[data-avg-card]');
  }

  onQtyChange(sel) {
    const card = this.card(sel);
    if (!card) return;
    const id = card.dataset.variantId;
    const n = parseInt(sel.value, 10) || 0;

    if (n > 0) this.qty.set(id, n);
    else this.qty.delete(id);

    // Só o cartão afetado é atualizado; o resto da grade não é tocado.
    const band = card.querySelector('[data-avg-selected]');
    if (band) band.hidden = n === 0;

    this.refresh();
  }

  onPrimary(pill) {
    const value = pill.dataset.value;

    this.root.querySelectorAll('[data-primary-pill]').forEach((p) => {
      const on = p === pill;
      p.classList.toggle('is-active', on);
      p.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    // Filtra a grade. As quantidades das combinações ocultas continuam no
    // estado — o cliente pode montar um pedido com mais de uma espessura.
    this.root.querySelectorAll('[data-avg-card]').forEach((c) => {
      c.hidden = c.dataset.primary !== value;
    });
  }

  /* Reaproveita o formulário "Avise-me" que já existe na página, apontando-o
     para a variante do cartão clicado. */
  onNotify(btn) {
    const card = this.card(btn);
    const notify = document.querySelector('[data-notify]');
    if (!notify) return;

    const vTitle = btn.dataset.variantTitle || (card && card.dataset.title) || '';
    const vSku = btn.dataset.variantSku || '';
    const fieldTitle = notify.querySelector('[data-notify-variant]');
    const fieldSku = notify.querySelector('[data-notify-sku]');
    if (fieldTitle) fieldTitle.value = vTitle;
    if (fieldSku) fieldSku.value = vSku;

    notify.hidden = false;
    notify.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = notify.querySelector('input[type="email"]');
    if (input) setTimeout(() => input.focus(), 350);
  }

  totals() {
    let units = 0;
    let cents = 0;
    this.qty.forEach((n, id) => {
      const card = this.root.querySelector('[data-avg-card][data-variant-id="' + id + '"]');
      units += n;
      if (card) cents += (parseInt(card.dataset.price, 10) || 0) * n;
    });
    return { units, cents };
  }

  refresh() {
    const { units, cents } = this.totals();
    const on = units > 0;

    if (this.buyBtn) {
      this.buyBtn.disabled = !on || this.busy;
      this.buyBtn.setAttribute('aria-disabled', !on || this.busy ? 'true' : 'false');
    }
    if (this.buyLabel && !this.busy) {
      this.buyLabel.textContent = on
        ? 'Comprar selecionados (' + units + ')'
        : 'Comprar selecionados';
    }
    if (this.status && !this.busy) {
      this.status.className = 'avg__status';
      this.status.textContent = on
        ? units + (units === 1 ? ' item selecionado — ' : ' itens selecionados — ') + BRL.format(cents / 100)
        : '';
    }
  }

  items() {
    const out = [];
    this.qty.forEach((n, id) => {
      if (n > 0) out.push({ id: Number(id), quantity: n });
    });
    return out;
  }

  cartSectionIds() {
    return Array.prototype.slice
      .call(document.querySelectorAll('cart-items-component'))
      .map((c) => c.dataset.sectionId)
      .filter(Boolean);
  }

  async refreshCart() {
    const comp = document.querySelector('cart-items-component');
    if (comp) {
      await customElements.whenDefined('cart-items-component');
      if (typeof comp.fetchCartData === 'function') return comp.fetchCartData();
    }
    const res = await fetch(this.cartUrl + '.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return res.json();
  }

  async onBuy() {
    if (this.busy) return; // trava contra clique repetido
    const items = this.items();
    if (items.length === 0) return;

    this.busy = true;
    if (this.buyBtn) this.buyBtn.disabled = true;
    if (this.buyLabel) this.buyLabel.textContent = 'Adicionando...';
    if (this.status) {
      this.status.className = 'avg__status';
      this.status.textContent = '';
    }

    const units = items.reduce((s, i) => s + i.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.root.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: this.cartSectionIds().join(',') }),
      });
      const data = await res.json();

      if (data.status) {
        // Erro do carrinho (ex.: estoque acabou entre a seleção e o envio).
        // As quantidades são preservadas para nova tentativa.
        this.root.dispatchEvent(
          new CartErrorEvent({ error: data.message || 'Erro ao adicionar', code: 'INVALID' })
        );
        deferred.reject(new Error(data.message || 'add failed'));
        this.fail(data.description || data.message || 'Não foi possível adicionar. Tente novamente.');
        return;
      }

      const cart = await this.refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'variant-grid',
          itemCount: units,
          didError: false,
          sections: data.sections,
        },
      });

      this.clear();
    } catch (err) {
      this.root.dispatchEvent(
        new CartErrorEvent({ error: (err && err.message) || 'Erro de rede', code: 'SERVICE_UNAVAILABLE' })
      );
      deferred.reject(err);
      this.fail('Falha de conexão. Sua seleção foi mantida — tente novamente.');
    } finally {
      this.busy = false;
      this.refresh();
    }
  }

  fail(msg) {
    if (!this.status) return;
    this.status.className = 'avg__status avg__status--err';
    this.status.textContent = msg;
  }

  /* Zera a seleção apenas após sucesso integral. */
  clear() {
    this.qty.clear();
    this.root.querySelectorAll('[data-avg-qty]').forEach((s) => {
      s.value = '0';
    });
    this.root.querySelectorAll('[data-avg-selected]').forEach((b) => {
      b.hidden = true;
    });
    if (this.status) {
      this.status.className = 'avg__status avg__status--ok';
      this.status.textContent = 'Itens adicionados ao carrinho.';
    }
  }
}

function boot(ctx) {
  const scope = ctx && ctx.querySelectorAll ? ctx : document;
  scope.querySelectorAll('[data-variant-grid]').forEach((root) => {
    if (root.__avg) return;
    root.__avg = new VariantGrid(root);
    root.__avg.init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

document.addEventListener('shopify:section:load', (e) => boot(e.target));
